import { forwardRef } from 'react'

import { Tabs as TabsPrimitive } from '@base-ui/react/tabs'
import { tv, type VariantProps } from 'tailwind-variants'

import { cn } from '../../../utils/cn'

const tabsListVariants = tv({
  base: 'inline-flex w-fit items-center justify-center',
  variants: {
    variant: {
      default: 'bg-surface-200 gap-1 rounded-lg p-1',
      line: 'border-outline gap-1 border-b bg-transparent',
      'page-header':
        'border-outline relative flex w-full justify-start gap-4 border-b bg-transparent sm:gap-6 lg:gap-12'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
})

export type TabsListVariant = NonNullable<
  VariantProps<typeof tabsListVariants>['variant']
>

export type TabsProps = TabsPrimitive.Root.Props

export const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  ({ className, ...props }, ref) => (
    <TabsPrimitive.Root
      ref={ref}
      data-slot='tabs'
      className={cn('flex data-[orientation=horizontal]:flex-col', className)}
      {...props}
    />
  )
)
Tabs.displayName = 'Tabs'

export type TabsListProps = TabsPrimitive.List.Props &
  VariantProps<typeof tabsListVariants>

export const TabsList = forwardRef<HTMLDivElement, TabsListProps>(
  ({ variant = 'default', className, ...props }, ref) => (
    <TabsPrimitive.List
      ref={ref}
      data-slot='tabs-list'
      data-variant={variant}
      className={cn(
        'group/tabs-list',
        tabsListVariants({ variant }),
        className
      )}
      {...props}
    />
  )
)
TabsList.displayName = 'TabsList'

export type TabsIndicatorProps = TabsPrimitive.Indicator.Props

export const TabsIndicator = forwardRef<HTMLSpanElement, TabsIndicatorProps>(
  ({ className, ...props }, ref) => (
    <TabsPrimitive.Indicator
      ref={ref}
      data-slot='tabs-indicator'
      className={className}
      {...props}
    />
  )
)
TabsIndicator.displayName = 'TabsIndicator'

export type TabsTriggerProps = TabsPrimitive.Tab.Props

export const TabsTrigger = forwardRef<HTMLElement, TabsTriggerProps>(
  ({ className, ...props }, ref) => (
    <TabsPrimitive.Tab
      ref={ref}
      data-slot='tabs-trigger'
      className={cn(
        // base
        'relative inline-flex items-center justify-center whitespace-nowrap px-3 py-1.5 text-sm font-medium transition-all',
        'text-surface-body hover:text-surface-title-50',
        // focus
        'focus-visible:ring-primary-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        // disabled
        'disabled:pointer-events-none disabled:opacity-50',
        'aria-disabled:pointer-events-none aria-disabled:opacity-50',
        // default variant — active: pill with background
        'group-data-[variant=default]/tabs-list:data-active:bg-surface group-data-[variant=default]/tabs-list:data-active:text-surface-title-50 group-data-[variant=default]/tabs-list:data-active:shadow-flat group-data-[variant=default]/tabs-list:data-active:rounded-md',
        // line variant — active: underline indicator
        'after:bg-primary after:absolute after:opacity-0 after:transition-opacity',
        'group-data-[variant=line]/tabs-list:after:inset-x-0 group-data-[variant=line]/tabs-list:after:-bottom-px group-data-[variant=line]/tabs-list:after:h-0.5',
        'group-data-[variant=line]/tabs-list:data-active:text-surface-title-50 group-data-[variant=line]/tabs-list:data-active:after:opacity-100',
        // Ømni page navigation uses the same tab behavior with its own type
        // scale and a 4px rounded active line measured in Figma.
        'group-data-[variant=page-header]/tabs-list:font-heading group-data-[variant=page-header]/tabs-list:text-title-lg group-data-[variant=page-header]/tabs-list:font-normal',
        'group-data-[variant=page-header]/tabs-list:px-0 group-data-[variant=page-header]/tabs-list:pb-[7px] group-data-[variant=page-header]/tabs-list:pt-0 group-data-[variant=page-header]/tabs-list:lg:text-[20px]/[30px]',
        'group-data-[variant=page-header]/tabs-list:data-active:text-surface-title-50 group-data-[variant=page-header]/tabs-list:data-active:font-medium group-data-[variant=page-header]/tabs-list:data-active:after:opacity-100',
        className
      )}
      {...props}
    />
  )
)
TabsTrigger.displayName = 'TabsTrigger'

export type TabsContentProps = TabsPrimitive.Panel.Props

export const TabsContent = forwardRef<HTMLDivElement, TabsContentProps>(
  ({ className, ...props }, ref) => (
    <TabsPrimitive.Panel
      ref={ref}
      data-slot='tabs-content'
      className={cn('mt-2 flex-1 outline-none', className)}
      {...props}
    />
  )
)
TabsContent.displayName = 'TabsContent'
